import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Users, Eye, MousePointerClick, TrendingUp, Radio,
  RefreshCw, ExternalLink, AlertTriangle, PlugZap,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell,
} from "recharts";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";

const GOLD = "#D4AF37";
const DEVICE_COLORS = { desktop: "#D4AF37", mobile: "#FDE047", tablet: "#B08D3F", smart_tv: "#8B7355" };
const PIE_COLORS = ["#D4AF37", "#FDE047", "#B08D3F", "#8B7355", "#a1a1aa"];
const CONV_LABELS = { reservation_click: "Reservation Clicks", phone_click: "Phone Clicks", sms_click: "Text Clicks" };

const nf = (n) => (n == null ? "—" : Number(n).toLocaleString());
const tooltipStyle = { background: "#121214", border: "1px solid #ffffff20", borderRadius: 4, color: "#fff", fontSize: 13 };

function Panel({ children, className = "", testid }) {
  return (
    <div data-testid={testid} className={`bg-[#121214] border border-white/10 rounded-sm p-6 ${className}`}>
      {children}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, testid, live }) {
  return (
    <Panel testid={testid} className="hover:border-[#D4AF37]/30 transition-colors">
      <div className="flex items-center justify-between">
        <Icon size={22} strokeWidth={1.5} className="gold-text" />
        {live && (
          <span className="flex items-center gap-1.5 text-[10px] tracking-label uppercase text-emerald-400">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
            </span>
            Live
          </span>
        )}
      </div>
      <div className="font-heading text-4xl mt-4" data-testid={`${testid}-value`}>{value}</div>
      <div className="text-xs tracking-label uppercase text-zinc-500 mt-1">{label}</div>
      {sub && <div className="text-zinc-500 text-sm mt-1">{sub}</div>}
    </Panel>
  );
}

export default function AdminAnalytics() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadOverview = useCallback(() => {
    setRefreshing(true);
    api.get("/analytics/overview")
      .then((r) => setOverview(r.data))
      .catch(() => setOverview({ configured: true, error: "Failed to load analytics." }))
      .finally(() => setRefreshing(false));
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  useEffect(() => {
    const fetchRt = () => api.get("/analytics/realtime").then((r) => setRealtime(r.data)).catch(() => {});
    fetchRt();
    const iv = setInterval(fetchRt, 30000);
    return () => clearInterval(iv);
  }, []);

  const header = (
    <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
      <div>
        <button onClick={() => navigate("/admin")} data-testid="analytics-back-btn" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors mb-3">
          <ArrowLeft size={16} /> Back to Admin
        </button>
        <h1 className="font-heading text-4xl font-light tracking-tight">Website Analytics</h1>
        <p className="text-zinc-500 font-light mt-1">Live traffic &amp; engagement, powered by Google Analytics 4.</p>
      </div>
      <button onClick={loadOverview} data-testid="analytics-refresh-btn" className="flex items-center gap-2 border border-white/15 rounded-sm px-4 py-2 text-sm text-zinc-300 hover:border-[#D4AF37]/50 transition-colors">
        <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} /> Refresh
      </button>
    </div>
  );

  const shell = (children) => (
    <div className="min-h-screen bg-background text-white" data-testid="admin-analytics">
      <div className="border-b border-white/10 bg-[#0d0d0f] px-6 md:px-10 py-5">
        <Logo imgClass="h-8 w-8" textClass="text-lg" />
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {header}
        {children}
      </div>
    </div>
  );

  // Loading
  if (overview === null) {
    return shell(
      <div className="font-mono text-sm tracking-widest gold-text animate-pulse" data-testid="analytics-loading">LOADING…</div>
    );
  }

  // Not connected — no fake data, clear instructions
  if (overview.configured === false) {
    return shell(
      <Panel testid="analytics-not-connected" className="max-w-3xl border-[#D4AF37]/30">
        <PlugZap size={30} strokeWidth={1.5} className="gold-text mb-4" />
        <h2 className="font-heading text-2xl font-light mb-2">Connect Google Analytics to see live data</h2>
        <p className="text-zinc-400 leading-relaxed">
          The GA4 tracking tag <span className="gold-text font-mono">G-5VMRF63Z4P</span> is installed on the website, but the
          dashboard still needs read access to your Analytics account. To keep numbers accurate, no data is shown until it is connected.
        </p>
        <p className="text-zinc-300 mt-5 font-medium">Provide these to finish setup:</p>
        <ol className="list-decimal list-inside text-zinc-400 mt-2 space-y-1.5 leading-relaxed">
          <li>Your GA4 <span className="text-white">Property ID</span> (Admin → Property Settings — a number).</li>
          <li>A Google Cloud <span className="text-white">service-account JSON key</span> with the Analytics Data API enabled.</li>
          <li>Add that service-account email as a <span className="text-white">Viewer</span> on the GA4 property.</li>
        </ol>
        <a href="https://analytics.google.com/analytics/web/" target="_blank" rel="noreferrer" data-testid="analytics-open-ga" className="inline-flex items-center gap-2 mt-6 bg-[#D4AF37] text-black hover:bg-[#E0BC4A] transition-colors rounded-sm px-[3px] py-[1px] text-sm font-bold">
          Open Google Analytics <ExternalLink size={14} />
        </a>
      </Panel>
    );
  }

  const s = overview.sessions || {};
  const today = s.today || {};
  const d7 = s.last_7_days || {};
  const d30 = s.last_30_days || {};
  const devices = overview.devices || [];
  const traffic = overview.traffic_sources || [];
  const topPages = overview.top_pages || [];
  const conversions = overview.conversions || [];
  const totalConv = conversions.reduce((a, c) => a + (c.count || 0), 0);
  const activeUsers = realtime?.active_users ?? 0;

  return shell(
    <div className="space-y-8">
      {overview.error && (
        <Panel testid="analytics-error" className="border-red-500/30 bg-red-500/5 flex items-start gap-3">
          <AlertTriangle size={20} className="text-red-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-red-300 font-medium">Could not fetch some reports.</p>
            <p className="text-zinc-400 text-sm mt-1 font-mono break-all">{overview.error}</p>
            <p className="text-zinc-500 text-sm mt-2">Confirm the service account has Viewer access to the correct GA4 property.</p>
          </div>
        </Panel>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard testid="stat-active-users" icon={Radio} label="Active Users Now" value={nf(activeUsers)} sub="Last 30 min" live />
        <StatCard testid="stat-sessions-30" icon={TrendingUp} label="Sessions · 30 days" value={nf(d30.sessions)} sub={`${nf(today.sessions)} today`} />
        <StatCard testid="stat-pageviews-30" icon={Eye} label="Page Views · 30 days" value={nf(d30.pageviews)} sub={`${nf(today.pageviews)} today`} />
        <StatCard testid="stat-conversions-30" icon={MousePointerClick} label="Key Actions · 30 days" value={nf(totalConv)} sub="Reserve / call / text" />
      </div>

      {/* Sessions & page views table */}
      <Panel testid="analytics-sessions-table">
        <h2 className="font-heading text-2xl font-light mb-5">Sessions &amp; Page Views</h2>
        <div className="overflow-hidden rounded-sm border border-white/10">
          <div className="grid grid-cols-3 gap-2 px-4 py-3 bg-white/[0.04] text-[11px] tracking-label uppercase gold-text">
            <span>Period</span><span className="text-right">Sessions</span><span className="text-right">Page Views</span>
          </div>
          {[["Today", today], ["Last 7 days", d7], ["Last 30 days", d30]].map(([label, v]) => (
            <div key={label} className="grid grid-cols-3 gap-2 items-center px-4 py-4 border-t border-white/[0.06]" data-testid={`sessions-row-${label.replace(/\s+/g, "-").toLowerCase()}`}>
              <span className="text-white font-medium text-sm">{label}</span>
              <span className="text-right font-heading text-xl">{nf(v.sessions)}</span>
              <span className="text-right font-heading text-xl gold-text">{nf(v.pageviews)}</span>
            </div>
          ))}
        </div>
      </Panel>

      {/* Charts row */}
      <div className="grid lg:grid-cols-2 gap-8">
        <Panel testid="analytics-traffic-sources">
          <h2 className="font-heading text-2xl font-light mb-5">Traffic Sources <span className="text-zinc-500 text-sm">· 30 days</span></h2>
          {traffic.length === 0 ? (
            <p className="text-zinc-500 text-sm py-12 text-center">No session data yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={traffic} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid horizontal={false} stroke="#ffffff12" />
                <XAxis type="number" stroke="#71717a" fontSize={12} allowDecimals={false} />
                <YAxis type="category" dataKey="channel" stroke="#a1a1aa" fontSize={12} width={110} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#ffffff08" }} />
                <Bar dataKey="sessions" fill={GOLD} radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>

        <Panel testid="analytics-devices">
          <h2 className="font-heading text-2xl font-light mb-5">Devices <span className="text-zinc-500 text-sm">· 30 days</span></h2>
          {devices.length === 0 ? (
            <p className="text-zinc-500 text-sm py-12 text-center">No device data yet.</p>
          ) : (
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <ResponsiveContainer width="60%" height={220} minWidth={180}>
                <PieChart>
                  <Pie data={devices} dataKey="sessions" nameKey="device" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2} stroke="none">
                    {devices.map((d, i) => <Cell key={d.device} fill={DEVICE_COLORS[d.device] || PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                {devices.map((d, i) => (
                  <div key={d.device} className="flex items-center gap-2 text-sm" data-testid={`device-legend-${d.device}`}>
                    <span className="h-3 w-3 rounded-sm" style={{ background: DEVICE_COLORS[d.device] || PIE_COLORS[i % PIE_COLORS.length] }} />
                    <span className="capitalize text-zinc-300">{d.device}</span>
                    <span className="text-zinc-500">· {nf(d.sessions)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Panel>
      </div>

      {/* Top pages + conversions */}
      <div className="grid lg:grid-cols-2 gap-8">
        <Panel testid="analytics-top-pages">
          <h2 className="font-heading text-2xl font-light mb-5">Top Pages <span className="text-zinc-500 text-sm">· 30 days</span></h2>
          {topPages.length === 0 ? (
            <p className="text-zinc-500 text-sm py-8 text-center">No page-view data yet.</p>
          ) : (
            <div className="space-y-1">
              {topPages.map((p) => (
                <div key={p.path} className="flex items-center justify-between px-3 py-2.5 rounded-sm hover:bg-white/[0.03]" data-testid={`top-page-${p.path}`}>
                  <span className="text-zinc-300 text-sm font-mono truncate mr-3">{p.path}</span>
                  <span className="text-white font-heading text-lg shrink-0">{nf(p.views)}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel testid="analytics-conversions">
          <h2 className="font-heading text-2xl font-light mb-5">Key Actions <span className="text-zinc-500 text-sm">· 30 days</span></h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {["reservation_click", "phone_click", "sms_click"].map((ev) => {
              const found = conversions.find((c) => c.event === ev);
              return (
                <div key={ev} className="bg-[#0d0d0f] border border-white/10 rounded-sm p-5 text-center" data-testid={`conversion-${ev}`}>
                  <div className="font-heading text-3xl gold-text">{nf(found?.count ?? 0)}</div>
                  <div className="text-xs tracking-label uppercase text-zinc-500 mt-2">{CONV_LABELS[ev]}</div>
                </div>
              );
            })}
          </div>
          <p className="text-zinc-600 text-xs mt-5 leading-relaxed">
            Counts of visitors tapping the Reserve, Call, and Text buttons. New actions may take a few hours to appear in GA4.
          </p>
        </Panel>
      </div>
    </div>
  );
}
